import React, { Suspense, useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { LayoutDashboard, FileSearch, FilePlus2, ListChecks, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DemoProvider } from '../demo/DemoContext';
import LoadingSpinner from '../components/LoadingSpinner';
import peppermintLogo from '../assets/peppermint-logo.svg';

const NAV = [
  { name: 'Overview', href: '/demo/overview', icon: LayoutDashboard },
  { name: 'CTA Intelligence', href: '/demo/cta', icon: FileSearch },
  { name: 'Generate Invoice', href: '/demo/generate', icon: FilePlus2 },
  { name: 'Invoices', href: '/demo/invoices', icon: ListChecks },
];

const DemoLayout: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();

  return (
    <DemoProvider>
      <div className="flex h-screen overflow-hidden bg-slate-100">
        {/* Mobile backdrop */}
        {open && <div className="fixed inset-0 z-40 bg-slate-600/75 lg:hidden" onClick={() => setOpen(false)} />}

        {/* Sidebar */}
        <div
          className={`fixed inset-y-0 left-0 z-50 w-64 transform bg-white shadow-lg transition-transform duration-300 lg:static lg:translate-x-0 ${
            open ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex h-16 items-center justify-between bg-slate-900 px-5">
            <Link to="/demo/overview" onClick={() => setOpen(false)} className="flex items-center">
              <img src={peppermintLogo} alt="Peppermint" className="h-7 w-auto" />
            </Link>
            <button className="text-white lg:hidden" onClick={() => setOpen(false)}>
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="mt-8 px-4">
            <ul className="space-y-2">
              {NAV.map((item) => {
                const Icon = item.icon;
                const current = pathname === item.href || (item.href === '/demo/overview' && pathname === '/demo');
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      onClick={() => setOpen(false)}
                      className={`flex items-center rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                        current ? 'border-r-4 border-emerald-600 bg-emerald-50 text-emerald-700' : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <Icon className={`mr-3 h-5 w-5 ${current ? 'text-emerald-600' : 'text-slate-400'}`} />
                      {item.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        {/* Main */}
        <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
          <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 md:px-6">
            <div className="flex items-center gap-3">
              <button className="text-slate-500 lg:hidden" onClick={() => setOpen(true)}>
                <Menu className="h-6 w-6" />
              </button>
              <span className="text-sm font-medium text-slate-500">Demo Research Associates</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              {user?.email && <span className="hidden sm:inline">{user.email}</span>}
              <button onClick={() => signOut()} className="rounded-md border border-slate-300 px-3 py-1 hover:bg-slate-50">
                Sign out
              </button>
            </div>
          </header>
          <main>
            <div className="mx-auto max-w-screen-2xl p-4 md:p-6 2xl:p-8">
              <Suspense fallback={<LoadingSpinner />}>
                <Outlet />
              </Suspense>
            </div>
          </main>
        </div>
      </div>
    </DemoProvider>
  );
};

export default DemoLayout;
